import React, { useState, useEffect } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import NotFound from '../../components/UI/NotFound';
import { HiOutlinePlus, HiOutlineRefresh } from 'react-icons/hi';
import { FaEdit, FaTrashAlt, FaTimes, FaList, FaFolder, FaToggleOn, FaToggleOff, FaCopy, FaEye, FaTimesCircle, FaThumbtack } from 'react-icons/fa';

// View Modal Component
const ViewModal = ({ category, onClose }) => {
    if (!category) return null;

    // Format date to a readable format
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="p-5 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-semibold text-gray-800">Category Details</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <FaTimesCircle size={24} />
                    </button>
                </div>

                <div className="p-5 space-y-6">
                    {/* Category Info */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">{category.category_name || 'Untitled Category'}</h3>

                        <div className="space-y-3">
                            {/* Status */}
                            <div className="flex items-start">
                                <div className="w-28 text-sm font-medium text-gray-500">Status:</div>
                                <div>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${category.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {category.status ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>

                            {/* Pin Status */}
                            <div className="flex items-start">
                                <div className="w-28 text-sm font-medium text-gray-500">Pin:</div>
                                <div>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${category.pin ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
                                        {category.pin ? 'Pin' : 'Not Pin'}
                                    </span>
                                </div>
                            </div>

                            {/* Lists Count */}
                            <div className="flex items-start">
                                <div className="w-28 text-sm font-medium text-gray-500">Lists Count:</div>
                                <div className="flex items-center">
                                    <FaList className="text-main-blue mr-2" />
                                    <span className="text-sm text-gray-900">{category.totalLists || 0}</span>
                                </div>
                            </div>

                            {/* Created At */}
                            <div className="flex items-start">
                                <div className="w-28 text-sm font-medium text-gray-500">Created:</div>
                                <div className="flex-1 text-sm text-gray-900">
                                    {category.createdAt ? formatDate(category.createdAt) : 'N/A'}
                                </div>
                            </div>

                            {/* Updated At */}
                            <div className="flex items-start">
                                <div className="w-28 text-sm font-medium text-gray-500">Updated:</div>
                                <div className="flex-1 text-sm text-gray-900">
                                    {category.updatedAt ? formatDate(category.updatedAt) : 'N/A'}
                                </div>
                            </div>

                            {/* ID */}
                            <div className="flex items-start gap-2">
                                <div className="w-28 text-sm font-medium text-gray-500">Category ID:</div>
                                <div className="flex-1 text-sm text-gray-500 font-mono overflow-hidden text-ellipsis">
                                    {category._id}
                                </div>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(category._id);
                                        toast.success('ID copied to clipboard!');
                                    }}
                                    className="flex items-center px-2 py-1 bg-gray-50 text-gray-600 rounded-md hover:bg-gray-100 transition-colors flex-shrink-0"
                                    title="Copy ID"
                                >
                                    <FaCopy className="mr-1" />
                                    ID
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-5 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const Category = () => {
    const [loader, setLoader] = useState(false);
    const [categories, setCategories] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [categoryName, setCategoryName] = useState('');
    const [categoryStatus, setCategoryStatus] = useState(true);
    const [categoryPin, setCategoryPin] = useState(false);
    const [error, setError] = useState(null);
    const [editCategory, setEditCategory] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState([]);
    // State for view modal
    const [viewCategory, setViewCategory] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getCategory();
            if (resp?.status) {
                setCategories(resp?.data);
                setTotalRecords(resp?.total);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Fetch error:", error.response?.data || error.message);
            toast.error("Failed to fetch categories");
        } finally {
            setLoader(false);
        }
    };

    const resetForm = () => {
        setCategoryName('');
        setCategoryStatus(true);
        setCategoryPin(false);
        setError(null);
        setEditCategory(null);
    };

    const handleSubmit = async (e, categoryId = null) => {
        e.preventDefault();

        if (!categoryName?.trim()) {
            setError('Category name is required.');
            return;
        }

        setLoader(true);
        try {
            const formData = {
                category_name: categoryName,
                status: categoryStatus,
                pin: categoryPin
            };

            let resp;
            if (categoryId) {
                resp = await Calling.updateCategory(categoryId, formData);
            } else {
                resp = await Calling.addCategory(formData);
            }

            if (resp?.status) {
                toast.success(resp?.message);
                resetForm();
                fetchCategories();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Submission error:", error.response?.data || error.message);
            toast.error("Something went wrong.");
        } finally {
            setLoader(false);
        }
    };

    const handleEdit = (category) => {
        setEditCategory(category._id);
        setCategoryName(category.category_name);
        setCategoryStatus(category.status);
        setCategoryPin(category.pin || false);
        setError(null);

        // Scroll to the form with smooth animation
        setTimeout(() => {
            const formElement = document.querySelector('.category-form');
            if (formElement) {
                formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                // Fallback if the form element isn't found
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        }, 100); // Small delay to ensure state updates have completed
    };

    const handleDelete = async () => {
        if (selectedCategories.length === 0) {
            toast.error('Please select at least one category to delete.');
            return;
        }

        const content = "You are about to delete selected categories. To confirm, please solve this math problem.";
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');

        setLoader(true);
        try {
            const resp = await Calling.deleteCategory({ id: selectedCategories });
            if (resp?.status) {
                toast.success(resp?.message);
                setCategories(categories.filter((category) => !selectedCategories.includes(category._id)));
                setSelectedCategories([]);
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Delete error:", error.response?.data || error.message);
            toast.error("Failed to delete categories.");
        } finally {
            setLoader(false);
        }
    };

    const handleUpdateStatus = async (id) => {
        const content = "You are about to update this category's status. To confirm, please solve this math problem.";
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');

        setLoader(true);
        try {
            // Get the current category
            const currentCategory = categories.find(category => category._id === id);

            // Create formData with updated status (toggled)
            const formData = {
                category_name: currentCategory.category_name,
                status: !currentCategory.status,
                pin: currentCategory.pin || false
            };

            const resp = await Calling.updateCategory(id, formData);
            if (resp?.status) {
                toast.success(resp?.message);
                fetchCategories();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Status update error:", error.response?.data || error.message);
            toast.error("Failed to update status.");
        } finally {
            setLoader(false);
        }
    };

    const handleUpdatePin = async (id) => {
        const content = "You are about to update this category's pin status. To confirm, please solve this math problem.";
        const check = generateCaptcha(content);
        if (!check) return toast.error('Incorrect answer.');

        setLoader(true);
        try {
            // Get the current category
            const currentCategory = categories.find(category => category._id === id);

            // Create formData with updated pin status (toggled)
            const formData = {
                category_name: currentCategory.category_name,
                status: currentCategory.status,
                pin: !(currentCategory.pin || false)
            };

            const resp = await Calling.updateCategory(id, formData);
            if (resp?.status) {
                toast.success(resp?.message);
                fetchCategories();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Pin status update error:", error.response?.data || error.message);
            toast.error("Failed to update pin status.");
        } finally {
            setLoader(false);
        }
    };

    const handleSelectCategory = (id) => {
        if (selectedCategories.includes(id)) {
            setSelectedCategories(selectedCategories.filter(categoryId => categoryId !== id));
        } else {
            setSelectedCategories([...selectedCategories, id]);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedCategories(categories.map(category => category._id));
        } else {
            setSelectedCategories([]);
        }
    };

    const handleCopyId = (id) => {
        navigator.clipboard.writeText(id);
        toast.success('ID copied to clipboard!');
    };

    const handleView = (category) => {
        setViewCategory(category);
        setShowViewModal(true);
    };

    const closeViewModal = () => {
        setShowViewModal(false);
        setViewCategory(null);
    };

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className="w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
                    <div className="mx-auto">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Category Management</h1>
                                <p className="mt-1 text-sm text-gray-500">Manage categories and their associated lists</p>
                            </div>
                            <button
                                onClick={fetchCategories}
                                className="flex items-center gap-2 px-4 py-2 bg-main-blue hover:bg-main-blue/80 rounded-lg text-white transition-colors duration-200 shadow-sm"
                            >
                                <HiOutlineRefresh className="text-lg" />
                                <span>Refresh</span>
                            </button>
                        </div>

                        {/* Add Category Card */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100 mb-6">
                            <div className="p-5 border-b border-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                        <HiOutlinePlus className="text-main-blue text-xl" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">
                                            {editCategory ? 'Edit Category' : 'Add New Category'}
                                        </h2>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            {editCategory ? 'Update existing category' : 'Create a new category for your application'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                <form onSubmit={(e) => handleSubmit(e, editCategory)} className="space-y-4 category-form">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="md:col-span-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaFolder className="text-main-blue text-sm" />
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Category Name
                                                </label>
                                            </div>
                                            <p className="text-xs text-gray-500 mb-2">Enter category name</p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <input
                                                type="text"
                                                name="categoryName"
                                                id="categoryName"
                                                value={categoryName}
                                                onChange={(e) => setCategoryName(e.target.value)}
                                                required
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue transition duration-200"
                                                placeholder="e.g., Photography, Art, Technology"
                                            />
                                            <p className={`text-xs mt-2 ${error ? 'text-red-500' : 'text-gray-500'}`}>
                                                {error ? error : ''}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="md:col-span-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaToggleOn className="text-main-blue text-sm" />
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Status
                                                </label>
                                            </div>
                                            <p className="text-xs text-gray-500 mb-2">Set category status</p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={categoryStatus}
                                                    onChange={() => setCategoryStatus(!categoryStatus)}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-11 h-6 bg-gray-200 rounded-full peer 
                                                peer-checked:after:translate-x-full peer-checked:after:border-white 
                                                after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                                after:bg-white after:border-gray-300 after:border after:rounded-full 
                                                after:h-5 after:w-5 after:transition-all dark:border-gray-600 
                                                peer-checked:bg-main-blue"></div>
                                                <span className="ml-3 text-sm font-medium text-gray-700">
                                                    {categoryStatus ? 'Active' : 'Inactive'}
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="md:col-span-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaThumbtack className="text-main-blue text-sm" />
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Pin Status
                                                </label>
                                            </div>
                                            <p className="text-xs text-gray-500 mb-2">Pin category to top</p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={categoryPin}
                                                    onChange={() => setCategoryPin(!categoryPin)}
                                                    className="sr-only peer"
                                                />
                                                <div className="w-11 h-6 bg-gray-200 rounded-full peer 
                                                peer-checked:after:translate-x-full peer-checked:after:border-white 
                                                after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                                after:bg-white after:border-gray-300 after:border after:rounded-full 
                                                after:h-5 after:w-5 after:transition-all dark:border-gray-600 
                                                peer-checked:bg-yellow-400"></div>
                                                <span className="ml-3 text-sm font-medium text-gray-700">
                                                    {categoryPin ? 'pin' : 'Not Pin'}
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-end gap-3 pt-2">
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                                        >
                                            Reset
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 transition-colors duration-200 flex items-center gap-2"
                                        >
                                            <HiOutlinePlus className="text-sm" />
                                            {editCategory ? 'Update Category' : 'Save Category'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Categories List Card */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100">
                            <div className="p-5 border-b border-gray-100">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2.5 bg-main-blue/10 rounded-lg">
                                            <FaFolder className="text-main-blue text-xl" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-800">Categories</h2>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                Manage your existing categories
                                                <span className="ml-1 px-2 py-0.5 bg-main-blue/10 text-main-blue rounded-full text-xs font-medium">
                                                    Total: {totalRecords}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                    {selectedCategories.length > 0 && (
                                        <button
                                            onClick={handleDelete}
                                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center gap-2"
                                        >
                                            <FaTrashAlt className="text-sm" />
                                            Delete Selected ({selectedCategories.length})
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                {categories.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 text-main-blue focus:ring-main-blue border-gray-300 rounded"
                                                            onChange={handleSelectAll}
                                                            checked={selectedCategories.length === categories.length && categories.length > 0}
                                                        />
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Category Name
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Lists Count
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Pin
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Created At
                                                    </th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {categories.map((category) => (
                                                    <tr key={category._id} className={`hover:bg-gray-50 ${category.pin ? 'bg-yellow-50' : ''}`}>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <input
                                                                type="checkbox"
                                                                className="h-4 w-4 text-main-blue focus:ring-main-blue border-gray-300 rounded"
                                                                onChange={() => handleSelectCategory(category._id)}
                                                                checked={selectedCategories.includes(category._id)}
                                                            />
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                {category.pin && <FaThumbtack className="text-yellow-500 mr-2" />}
                                                                <span className="text-sm font-medium text-gray-900">{category.category_name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <FaList className="text-main-blue mr-2" />
                                                                <span className="text-sm text-gray-900">{category.totalLists}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <label className="relative inline-flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={category.status}
                                                                    onChange={() => handleUpdateStatus(category._id)}
                                                                    className="sr-only peer"
                                                                />
                                                                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer 
                                                                peer-checked:after:translate-x-full peer-checked:after:border-white 
                                                                after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                                                after:bg-white after:border-gray-300 after:border after:rounded-full 
                                                                after:h-5 after:w-5 after:transition-all dark:border-gray-600 
                                                                peer-checked:bg-main-blue"></div>
                                                            </label>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <label className="relative inline-flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={category.pin || false}
                                                                    onChange={() => handleUpdatePin(category._id)}
                                                                    className="sr-only peer"
                                                                />
                                                                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer 
                                                                peer-checked:after:translate-x-full peer-checked:after:border-white 
                                                                after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                                                after:bg-white after:border-gray-300 after:border after:rounded-full 
                                                                after:h-5 after:w-5 after:transition-all dark:border-gray-600 
                                                                peer-checked:bg-yellow-400"></div>
                                                            </label>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-500">
                                                                {new Date(category.createdAt).toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric'
                                                                })}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap flex items-center gap-2 text-sm font-medium">
                                                            <button
                                                                onClick={() => handleView(category)}
                                                                className="flex items-center px-2 py-1 bg-main-blue/10 text-main-blue rounded-md hover:bg-main-blue/20 transition-colors"
                                                            >
                                                                <FaEye />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(category)}
                                                                className="flex items-center px-2 py-1 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            <button
                                                                onClick={() => handleCopyId(category._id)}
                                                                className="flex items-center px-2 py-1 bg-green-100 text-green-600 rounded-md hover:bg-green-200 transition-colors"
                                                                title="Copy ID"
                                                            >
                                                                <FaCopy />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <NotFound />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {showViewModal && <ViewModal category={viewCategory} onClose={closeViewModal} />}
        </>
    );
};

export default Category;